import React, { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col, Form, Card } from 'antd';
import styled from 'styled-components';
import { RemoveScroll } from 'react-remove-scroll';
import { Link } from 'react-router-dom';
import ContentStore from '../../../stores/ContentStore';
import ModalStore from '../../../stores/ModalStore';
import ProductCardStore from '../../../stores/product-stores/ProductCardStore';
import { ProductInfo } from '../../../swagger/api';
import FullPageModal from '../../modals/FullPageModal';
import PrivateLocalServices from './components/PrivateLocalServices';
import PrivateProductCardHeader from './components/PrivateProductCardHeader';
import { ReactComponent as CloseIcon } from '../../../layout/icons/close-green.svg';
import VehicleInformationTile from './components/VehicleInformationTile';
import PrivatePayment from './components/PrivatePayment';
import useIsMobileView from '../../../hooks/useIsMobileView';
import useStore from '../../../hooks/useStore';
import { OutlinedButton, PrimaryButton } from '../../../common-components/Buttons';
import ConfirmCloseProductModal from './modals/ConfirmCloseProductModal';
import { ReactComponent as DeleteIconRed } from '../../../layout/icons/delete-red.svg';
import { GreenLinkText } from '../../../layout/globalFonts';
import RouteUrlPaths from '../../../RouteUrlPaths';
// eslint-disable-next-line import/no-cycle
import TransactionsAndUsageTableDesktop from '../../transaction-features/transactions-and-usage/TransactionsAndUsageTableDesktop';
// eslint-disable-next-line import/no-cycle
import TransactionAndUsageCollapsable from './components/TransactionAndUsageCollapsable';
import { isANPR, isBizz } from '../../../utils/productTypeUtilities';
import useFetchListDataWithFunctionParameters from '../../../hooks/fetchDataHooks/useFetchListDataWithFunctionParameters';

const GreySection = styled.div<{ ismobileview: boolean }>`
  position: relative;
  background: #efefef;
  ${(props) => (props.ismobileview ? 'padding: 44px 20px 52px;' : 'padding: 44px 10.65% 52px;')}
`;
const GreyFooter = styled.div<{ ismobileview: boolean }>`
  position: relative;
  background: #efefef;
  ${(props) => (props.ismobileview ? 'padding: 46px 20px 20px 20px' : 'padding: 20px 10.65%;')}
`;

const SmallSection = styled.section<{ ismobileview: boolean }>`
  position: relative;
  background-color: #fff;
  padding: ${({ ismobileview }) => (ismobileview ? '30px' : '48px 56px')};
  border-radius: 8px;
  height: 100%;
  min-height: 290px;
  @media (max-width: 915px) {
    min-height: 270px;
  }
`;

const TransactionsSection = styled.div<{ ismobileview: boolean }>`
  position: relative;
  background: #fff;
  margin-top: 7.47vh;
  ${(props) => (props.ismobileview ? 'padding: 0px 20px' : 'padding: 0px 10.65%;')}
`;

const SectionCard = styled(Card)`
  margin-bottom: 24px;
  border: none;

  .ant-card-head {
    padding: 0;
  }

  .ant-card-body {
    padding-top: 0;
    padding-inline: 0 !important;
  }

  .ant-card-head-title {
    font-family: Campton Bold;
    font-weight: 700;
    font-size: 30px;
    color: #2d2d2d;
    padding-bottom: 10px;
    letter-spacing: -0.3px;
    line-height: 36px;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 0;
    }
  }
`;

const CancelNumberPlateAgreementButton = styled(OutlinedButton)`
  border: 2px solid #ff004a;
  color: #ff004a;
  font-weight: 550;
  font-size: 15px;
  line-height: 18px;
  height: fit-content;
  border: 1px solid #ff004a;
  padding-inline: 20px;
  background: transparent;

  &:hover {
    color: #ff004a;
    border: 1px solid #ff004a;
    outline: 1px solid #ff004a;
    background: transparent;
  }
  &:focus {
    color: #ff004a;
    border: 2px solid #ff004a;
    backgroupd: transparent;
  }
`;

interface PrivateProductCardModalProps {
  product: ProductInfo;
  contentStore: ContentStore;
  productCardStore: ProductCardStore;
}

const PrivateProductCardModal: FC<PrivateProductCardModalProps> = (props) => {
  const { product, contentStore, productCardStore } = props;
  const modalStore = useStore(ModalStore) as ModalStore;
  const { transactionsStore } = contentStore.rootStore;
  const [form] = Form.useForm();
  const isMobileView = useIsMobileView();

  const closeProductText = isBizz(product.productType)
    ? contentStore.cmsStore.getContent('CloseThisBizz')?.content
    : contentStore.cmsStore.getContent('CloseThisLicensePlate')?.content;

  const isLoading = useFetchListDataWithFunctionParameters(
    transactionsStore.identifierTransactions,
    true,
    transactionsStore.getIdentifierTransactions,
    product.identifierId!
  );

  const onCancelProductCardModal = () => {
    transactionsStore.identifierTransactions = [];
    productCardStore.closeModal();
  };

  const recentTransactions = transactionsStore.identifierTransactions.slice(0, 6);

  return (
    <RemoveScroll>
      <FullPageModal
        visible={productCardStore.isModalVisible}
        footer={null}
        onCancel={() => onCancelProductCardModal()}
        maskClosable
        closeIcon={<CloseIcon />}
        transitionName="modalFade"
        isopen={productCardStore.isAnimationOpen}
        ismobileview={isMobileView}
        isanimationdisabled={productCardStore.isAnimationDisabled}
      >
        <Form name="PrivateProductCardModal" initialValues={product} form={form}>
          <PrivateProductCardHeader contentStore={contentStore} product={product} form={form} />
        </Form>
        <GreySection ismobileview={isMobileView}>
          <Row gutter={[24, 24]}>
            {isANPR(product.productType) && (
              <Col xs={24} xl={8}>
                <SmallSection ismobileview={isMobileView}>
                  <VehicleInformationTile productInfo={product} />
                </SmallSection>
              </Col>
            )}
            <Col xs={24} xl={8}>
              <SmallSection ismobileview={isMobileView}>
                <PrivateLocalServices contentStore={contentStore} product={product} />
              </SmallSection>
            </Col>
            <Col xs={24} xl={8}>
              <SmallSection ismobileview={isMobileView}>
                <PrivatePayment contentStore={contentStore} product={product} />
              </SmallSection>
            </Col>
          </Row>
        </GreySection>
        {isMobileView ? (
          <>
            <TransactionAndUsageCollapsable contentStore={contentStore} productInfo={product} />
            <GreyFooter ismobileview={isMobileView} style={{ margin: '0' }}>
              <Row justify="center" style={{ marginBottom: '20px' }}>
                <CancelNumberPlateAgreementButton
                  onClick={() =>
                    modalStore.openModal(
                      <ConfirmCloseProductModal
                        contentStore={contentStore}
                        productInfo={product}
                        modalStore={modalStore}
                        productCardStore={productCardStore}
                      />
                    )
                  }
                >
                  <DeleteIconRed style={{ margin: '0px 10px 0px 0px' }} />
                  {closeProductText}
                </CancelNumberPlateAgreementButton>
              </Row>
              <Row justify="center" style={{ margin: '40px 0px' }}>
                <PrimaryButton onClick={() => productCardStore.closeModal()}>
                  {contentStore.cmsStore.getContent('Back')?.content}
                </PrimaryButton>
              </Row>
            </GreyFooter>
          </>
        ) : (
          <>
            <TransactionsSection ismobileview={isMobileView}>
              <SectionCard
                title={contentStore.cmsStore.getContent('ProductCardModalTrips')?.content}
              >
                <TransactionsAndUsageTableDesktop
                  contentStore={contentStore}
                  transactionsStore={transactionsStore}
                  showOutstandingOnly={false}
                  isTableOnProductCard
                  transactions={recentTransactions}
                  isLoading={isLoading}
                  isPrivateCustomer
                />
              </SectionCard>
            </TransactionsSection>
            <Row justify="center" style={{ margin: '20px 0px' }}>
              <GreenLinkText style={{ cursor: 'pointer' }}>
                <Link
                  to={{ pathname: `/${RouteUrlPaths.TransactionAndUsage}` }}
                  onClick={() => {
                    productCardStore.closeModal();
                    contentStore.rootStore.menuStore.setSelectedMenuItem(
                      RouteUrlPaths.TransactionAndUsage
                    );
                  }}
                >
                  {contentStore.cmsStore.getContent('SeeAllTransactionsAndUsage')?.content}
                </Link>
              </GreenLinkText>
            </Row>
            <GreyFooter ismobileview={isMobileView} style={{ margin: '0' }}>
              <Row justify="center" style={{ margin: '20px 0px' }} />
              <Row justify="end">
                <CancelNumberPlateAgreementButton
                  onClick={() =>
                    modalStore.openModal(
                      <ConfirmCloseProductModal
                        contentStore={contentStore}
                        productInfo={product}
                        modalStore={modalStore}
                        productCardStore={productCardStore}
                      />
                    )
                  }
                >
                  <DeleteIconRed style={{ margin: '0px 10px 0px 0px' }} />
                  {closeProductText}
                </CancelNumberPlateAgreementButton>
              </Row>
              <Row justify="center" style={{ margin: '40px 0px' }}>
                <PrimaryButton onClick={() => productCardStore.closeModal()}>
                  {contentStore.cmsStore.getContent('Back')?.content}
                </PrimaryButton>
              </Row>
            </GreyFooter>
          </>
        )}
      </FullPageModal>
    </RemoveScroll>
  );
};

export default observer(PrivateProductCardModal);
